import React from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import ScopeChangeEditCustomItemsDialogViewModel, {
  EditScopeChangeCustomItemViewModel,
} from './ScopeChangeEditCustomItemsDialogViewModel'
import styles from './ScopeChangesEditCustomItemsDialog.sass'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import cx from 'classnames'

interface Props {
  vm: ScopeChangeEditCustomItemsDialogViewModel
}

export default observer(function ScopeChangeEditCustomItemsDialog({
  vm,
}: Props) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton>
        <ModalSheetTitle>Edit custom service items</ModalSheetTitle>
      </ModalSheetHeader>
      <CustomItemHeadings />
      <ModalSheetContent>
        <div className={styles.itemsContainer}>
          {vm.editScopeChangeCustomItems.map((item) => {
            return (
              <EditScopeChangeCustomItem
                key={item.key}
                item={item}
                remove={vm.removeScopeChange}
              />
            )
          })}
          <div className={styles.addCustomItemContainer}>
            <div className={styles.customItemText}>
              Need more custom service items?
            </div>
            <Button
              type="text"
              dataTest="edit-custom-scope-changes-add-button"
              onClick={vm.addScopeChange}
            >
              Add new
            </Button>
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close} type="default">
            Close
          </Button>
          <Button
            onClick={vm.save}
            type="primary"
            dataTest="scope-change-request-edit"
            loading={vm.save.pending}
            disabled={vm.save.pending}
          >
            Save
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

const CustomItemHeadings = observer(function () {
  return (
    <div className={styles.subheading}>
      <span>Custom service items</span>
      <span>Quantity &nbsp;</span>
    </div>
  )
})

const EditScopeChangeCustomItem = observer(function ({
  item,
  remove,
}: {
  item: EditScopeChangeCustomItemViewModel
  remove: (key: string) => void
}) {
  return (
    <div className={styles.customItemInput}>
      <input
        type="text"
        className={cx(
          styles.input,
          styles.customItemName,
          item.validation.getError('name') && styles.hasError,
        )}
        placeholder="Enter your service type"
        value={item.name}
        onChange={(e) => item.setName(e.target.value)}
        data-test={`scope-change-name-${item.key}`}
      />

      <div
        className={cx(
          styles.customItemQuantityContainer,
          item.validation.getError('quantity') && styles.hasError,
        )}
      >
        <IconButton onClick={item.decrementQuantity}>
          <Icon name="subtract" size={16} />
        </IconButton>

        <input
          key="quantity"
          type="number"
          min="1"
          className={cx(styles.input, styles.customItemQuantity)}
          value={item.quantity}
          onChange={(e) => item.setQuantity(e.target.value)}
          data-test={`scope-change-quantity-${item.key}`}
        />

        <IconButton onClick={item.incrementQuantity}>
          <Icon name="add" size={16} />
        </IconButton>
      </div>

      <IconButton
        className={styles.removeCustomItemButton}
        onClick={() => remove(item.key)}
        dataTest={`request-scope-changes-remove-button-${item.key}`}
      >
        <Icon name="close" size={24} />
      </IconButton>
    </div>
  )
})
